import * as React from 'react'
import { useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listFacilities,
  createFacility,
  updateFacility,
  type Facility,
} from '@/features/facilities/api'
import { ManageBar } from '@/components/layout/ManageBar'
import { Button } from '@/components/primitives/Button'
import { Input } from '@/components/primitives/Input'
import { FormField } from '@/components/primitives/FormField'
import { PageLoader } from '@/components/feedback/PageLoader'
import { useToast } from '@/components/feedback/ToastProvider'
import { ApiError } from '@/lib/api'
import './ManagePages.css'

function locationLine(f: Facility): string {
  return [f.city, f.state, f.postal_code].filter(Boolean).join(', ') || '—'
}

export function FacilitiesPage(): React.JSX.Element {
  const toast = useToast()
  const qc = useQueryClient()

  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [postal, setPostal] = useState('')
  const [phone, setPhone] = useState('')

  const [editId, setEditId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editAddress, setEditAddress] = useState('')
  const [editCity, setEditCity] = useState('')
  const [editState, setEditState] = useState('')
  const [editPostal, setEditPostal] = useState('')
  const [editPhone, setEditPhone] = useState('')

  const facilitiesQuery = useQuery({ queryKey: ['facilities'], queryFn: listFacilities })

  const create = useMutation({
    mutationFn: () =>
      createFacility({
        name,
        address_line1: address || undefined,
        city: city || undefined,
        state: state || undefined,
        postal_code: postal || undefined,
        phone: phone || undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['facilities'] })
      setName('')
      setAddress('')
      setCity('')
      setState('')
      setPostal('')
      setPhone('')
      toast.success('Facility added', 'The new facility is now active.')
    },
    onError: (err) =>
      toast.error('Could not add facility', err instanceof ApiError ? err.message : 'Try again.'),
  })

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof updateFacility>[1] }) =>
      updateFacility(id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['facilities'] })
      setEditId(null)
      toast.success('Facility updated', '')
    },
    onError: (err) =>
      toast.error('Update failed', err instanceof ApiError ? err.message : 'Try again.'),
  })

  function onAdd(e: FormEvent): void {
    e.preventDefault()
    create.mutate()
  }

  function startEdit(f: Facility): void {
    setEditId(f.id)
    setEditName(f.name)
    setEditAddress(f.address_line1 ?? '')
    setEditCity(f.city ?? '')
    setEditState(f.state ?? '')
    setEditPostal(f.postal_code ?? '')
    setEditPhone(f.phone ?? '')
  }

  function saveEdit(f: Facility): void {
    update.mutate({
      id: f.id,
      patch: {
        name: editName || undefined,
        address_line1: editAddress || undefined,
        city: editCity || undefined,
        state: editState || undefined,
        postal_code: editPostal || undefined,
        phone: editPhone || undefined,
      },
    })
  }

  function toggleArchive(f: Facility): void {
    update.mutate({
      id: f.id,
      patch: { status: f.status === 'active' ? 'archived' : 'active' },
    })
  }

  const facilities = facilitiesQuery.data ?? []

  return (
    <div className="manage">
      <ManageBar />
      <main className="manage-main">
        <div className="manage-head">
          <h1 className="manage-title">Facilities</h1>
          <p className="manage-sub">
            Manage the physical sites in your organization. Each facility can have
            its own staff and residents.
          </p>
        </div>

        {/* Add facility */}
        <section className="manage-card">
          <h2 className="manage-card-title">Add a facility</h2>
          <form className="manage-fac-form" onSubmit={onAdd}>
            <FormField label="Facility name" htmlFor="fac-name" required>
              <Input
                id="fac-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Sunrise North"
              />
            </FormField>
            <FormField label="Address" htmlFor="fac-addr">
              <Input
                id="fac-addr"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="123 Main St"
              />
            </FormField>
            <FormField label="City" htmlFor="fac-city">
              <Input
                id="fac-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Chicago"
              />
            </FormField>
            <FormField label="State" htmlFor="fac-state">
              <Input
                id="fac-state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                placeholder="IL"
                maxLength={2}
              />
            </FormField>
            <FormField label="ZIP" htmlFor="fac-zip">
              <Input
                id="fac-zip"
                value={postal}
                onChange={(e) => setPostal(e.target.value)}
                placeholder="60601"
              />
            </FormField>
            <FormField label="Phone" htmlFor="fac-phone">
              <Input
                id="fac-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(312) 555-0100"
              />
            </FormField>
            <div className="manage-fac-submit">
              <Button type="submit" loading={create.isPending} disabled={!name.trim()}>
                Add facility
              </Button>
            </div>
          </form>
        </section>

        {/* List */}
        <div className="manage-card-table">
          <div className="manage-card-table-header">
            <div>
              <div className="manage-card-table-title">All facilities</div>
              {facilities.length > 0 && <div className="manage-card-table-sub">{facilities.length} {facilities.length !== 1 ? 'facilities' : 'facility'}</div>}
            </div>
          </div>
          {facilitiesQuery.isLoading ? (
            <div style={{ padding: 'var(--space-8)' }}><PageLoader /></div>
          ) : facilitiesQuery.isError ? (
            <div className="manage-empty-state"><p>Failed to load facilities. Please refresh.</p></div>
          ) : facilities.length === 0 ? (
            <div className="manage-empty-state"><p>No facilities yet. Add your first above.</p></div>
          ) : (
            <table className="manage-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Location</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th className="manage-col-action" style={{ width: 160 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {facilities.map((f) =>
                  editId === f.id ? (
                    <tr key={f.id} className="manage-edit-row">
                      <td>
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          placeholder="Facility name"
                          className="manage-edit-input"
                        />
                      </td>
                      <td>
                        <div className="manage-edit-location">
                          <Input
                            value={editAddress}
                            onChange={(e) => setEditAddress(e.target.value)}
                            placeholder="Address"
                            className="manage-edit-input"
                          />
                          <Input
                            value={editCity}
                            onChange={(e) => setEditCity(e.target.value)}
                            placeholder="City"
                            className="manage-edit-input"
                          />
                          <div className="manage-edit-row2">
                            <Input
                              value={editState}
                              onChange={(e) => setEditState(e.target.value)}
                              placeholder="ST"
                              maxLength={2}
                              className="manage-edit-input manage-edit-state"
                            />
                            <Input
                              value={editPostal}
                              onChange={(e) => setEditPostal(e.target.value)}
                              placeholder="ZIP"
                              className="manage-edit-input"
                            />
                          </div>
                        </div>
                      </td>
                      <td>
                        <Input
                          value={editPhone}
                          onChange={(e) => setEditPhone(e.target.value)}
                          placeholder="Phone"
                          className="manage-edit-input"
                        />
                      </td>
                      <td />
                      <td className="manage-col-action" style={{ width: 160 }}>
                        <div className="manage-edit-actions">
                          <Button
                            size="sm"
                            loading={update.isPending}
                            onClick={() => saveEdit(f)}
                            disabled={!editName.trim()}
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditId(null)}
                            disabled={update.isPending}
                          >
                            Cancel
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={f.id}>
                      <td>
                        <div className={'manage-name' + (f.status === 'archived' ? ' manage-archived' : '')}>
                          {f.name}
                        </div>
                      </td>
                      <td>
                        <span className="manage-email">
                          {f.address_line1 ? (
                            <>{f.address_line1}<br /></>
                          ) : null}
                          {locationLine(f)}
                        </span>
                      </td>
                      <td>
                        <span className="manage-email">{f.phone ?? '—'}</span>
                      </td>
                      <td>
                        <span className={`status-badge status-badge--${f.status === 'active' ? 'active' : 'inactive'}`}>
                          {f.status}
                        </span>
                      </td>
                      <td className="manage-col-action" style={{ width: 160 }}>
                        <div className="manage-row-actions">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => startEdit(f)}
                            disabled={update.isPending}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            loading={update.isPending && update.variables?.id === f.id}
                            onClick={() => toggleArchive(f)}
                          >
                            {f.status === 'active' ? 'Archive' : 'Restore'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  )
}
